import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Button } from '@/design-system'
import { useStudio3DData } from '../../hooks/useStudio3DData'
import { useStudioEngine } from '../../hooks/useStudioEngine'
import { useSpacesRoomScene } from './useSpacesRoomScene'
import { HallPhoto } from './buildSpacesRoom'
import { FURNITURE_CATALOG, ROOM_PRESETS, RoomPresetId } from './roomPresets'
import { APP_NAME } from '@/utils/constants'
import { GlassPanel, monoLabelSx, STUDIO_ACCENT } from '../../components/shared'
import { LogoMark } from '@/features/album/components/EgowallLogo'

const FALLBACK_PHOTOS: HallPhoto[] = [
  { url: '/marketing/landing-v2/a02-beach.jpg', ratio: 3 / 4 },
  { url: '/marketing/landing-v2/a06-baby.jpg', ratio: 3 / 4 },
  { url: '/marketing/landing-v2/a03-mountains.jpg', ratio: 4 / 3 },
  { url: '/marketing/landing-v2/a07-city.jpg', ratio: 3 / 4 },
]

/**
 * `/galleries/:galleryId/spaces-studio` — a real (if primitive-furniture)
 * take on placing things in a room: pick a room type, drop in furniture from
 * a catalog, drag it around, rotate, delete. Photos from the gallery hang on
 * the back wall as decor. This is the "prove furniture placement can work
 * on our own stack" prototype the Spaces vision panel points to — not a
 * shipped feature, nothing here saves.
 */
export function SpacesStudioScreen() {
  const { galleryId = '' } = useParams<{ galleryId: string }>()
  const navigate = useNavigate()
  const data = useStudio3DData(galleryId)
  const ready = data.seedReady

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const { scene } = useStudioEngine(canvasRef, ready)

  const photos: HallPhoto[] = useMemo(() => {
    const real = data.initialArts.filter((a) => !a.isPlaceholder && a.img).map((a) => ({ url: a.img, ratio: a.ratio || 4 / 3 }))
    return real.length > 0 ? real : FALLBACK_PHOTOS
  }, [data.initialArts])

  const [preset, setPresetState] = useState<RoomPresetId>('bedroom')
  const room = useSpacesRoomScene(scene, canvasRef, preset, photos)

  const changePreset = (next: RoomPresetId) => {
    setPresetState(next)
    room.setPreset(next)
  }

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!room.selectedId) return
      if (e.key === 'r' || e.key === 'R') room.rotateSelected()
      if (e.key === 'Delete' || e.key === 'Backspace') room.removeSelected()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room.selectedId])

  const backToStudio = () => navigate(`/galleries/${galleryId}/virtual-spaces`)

  if (data.isError) {
    return (
      <Stage sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ fontSize: 15, fontWeight: 600, mb: 1 }}>Gallery not found</Box>
          <Box sx={{ fontSize: 13, color: 'rgba(244,243,246,0.55)' }}>Open a real gallery from /galleries first.</Box>
        </Box>
      </Stage>
    )
  }

  return (
    <Stage>
      <Canvas ref={canvasRef} id='spaces-studio-canvas' />

      {!ready && (
        <Box sx={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, background: '#0b0a10' }}>
          <Typography sx={{ ...monoLabelSx, fontSize: 9 }}>LOADING…</Typography>
        </Box>
      )}

      <Top>
        <Brand>
          <LogoMark />
          <Typography component='span' sx={{ fontWeight: 750, letterSpacing: '-0.02em', fontSize: 15 }}>
            {APP_NAME}
          </Typography>
          <Typography component='span' sx={{ ...monoLabelSx, fontSize: 8, ml: 0.25 }}>
            SPACES · FURNITURE PROTOTYPE
          </Typography>
        </Brand>
        <Button variant='night' size='sm' onClick={backToStudio}>
          ← Back to vision
        </Button>
      </Top>

      <PresetRow>
        {(Object.keys(ROOM_PRESETS) as RoomPresetId[]).map((id) => (
          <PresetChip key={id} $on={id === preset} onClick={() => changePreset(id)}>
            {ROOM_PRESETS[id].label}
          </PresetChip>
        ))}
      </PresetRow>

      <Hint>{ROOM_PRESETS[preset].blurb}</Hint>

      <CatalogPanel>
        <Typography sx={{ ...monoLabelSx, fontSize: 8, mb: 1 }}>Add furniture</Typography>
        <CatalogGrid>
          {FURNITURE_CATALOG.map((f) => (
            <CatalogBtn key={f.kind} onClick={() => room.addFurniture(f.kind)}>
              {f.label}
            </CatalogBtn>
          ))}
        </CatalogGrid>

        {room.selectedId && (
          <>
            <Typography sx={{ ...monoLabelSx, fontSize: 8, mt: 2, mb: 1 }}>
              Selected — {room.items.find((i) => i.id === room.selectedId)?.kind}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <ToolBtn onClick={room.rotateSelected}>Rotate ⟳</ToolBtn>
              <ToolBtn onClick={room.removeSelected} sx={{ color: STUDIO_ACCENT.coral }}>
                Delete
              </ToolBtn>
            </Box>
          </>
        )}

        <Typography sx={{ fontSize: 11, color: 'rgba(244,243,246,0.4)', mt: 2, lineHeight: 1.5 }}>
          Drag orbits the room. Click a piece to select it, then drag it across the floor. R to rotate, Delete to remove.
        </Typography>
      </CatalogPanel>
    </Stage>
  )
}

const Stage = styled(Box)({
  position: 'fixed',
  inset: 0,
  overflow: 'hidden',
  background: '#050508',
  color: '#f4f3f6',
  fontFamily: 'var(--ds-font)',
})

const Canvas = styled('canvas')({
  position: 'fixed',
  inset: 0,
  width: '100%',
  height: '100%',
  outline: 'none',
  touchAction: 'none',
  display: 'block',
})

const Top = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '14px 18px',
})

const Brand = styled(Box)({ display: 'flex', alignItems: 'center', gap: 9 })

const PresetRow = styled(Box)({
  position: 'fixed',
  top: 62,
  left: 18,
  zIndex: 20,
  display: 'flex',
  gap: 6,
})

const PresetChip = styled('button', { shouldForwardProp: (p) => p !== '$on' })<{ $on?: boolean }>(({ $on }) => ({
  all: 'unset',
  height: 28,
  padding: '0 13px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  color: $on ? '#0d0d0f' : 'rgba(244,243,246,0.75)',
  background: $on ? 'var(--ds-gradient-spectrum)' : 'rgba(19,18,25,0.7)',
  border: $on ? 'none' : '1px solid rgba(244,243,246,0.14)',
  backdropFilter: 'blur(10px)',
}))

const Hint = styled(Typography)({
  position: 'fixed',
  top: 100,
  left: 18,
  zIndex: 20,
  maxWidth: 320,
  fontSize: 12.5,
  lineHeight: 1.5,
  color: 'rgba(244,243,246,0.55)',
  textShadow: '0 2px 10px rgba(0,0,0,0.5)',
})

const CatalogPanel = styled(GlassPanel)({
  position: 'fixed',
  right: 18,
  top: 62,
  bottom: 18,
  width: 260,
  zIndex: 20,
  padding: 16,
  overflowY: 'auto',
})

const CatalogGrid = styled(Box)({ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 })

const CatalogBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  height: 32,
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 600,
  textAlign: 'center',
  cursor: 'pointer',
  color: 'rgba(244,243,246,0.85)',
  background: 'rgba(244,243,246,0.05)',
  border: '1px solid rgba(244,243,246,0.1)',
  '&:hover': { background: 'rgba(244,243,246,0.1)' },
})

const ToolBtn = styled('button')({
  all: 'unset',
  height: 30,
  padding: '0 12px',
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  color: 'rgba(244,243,246,0.85)',
  background: 'rgba(244,243,246,0.08)',
  border: '1px solid rgba(244,243,246,0.14)',
  '&:hover': { background: 'rgba(244,243,246,0.15)' },
})
